//? Helpers used for snakeCase function
const wordSeparators = /[\s\u2000-\u206F\u2E00-\u2E7F\\'!"#$%&()*+,\-./:;<=>?@[\]^_`{|}~]+/
const capitals = /[A-Z\u00C0-\u00D6\u00D9-\u00DD]/g

/**
 *
 * @param str string to be converted to snake_case
 * @returns snake_case string
 */
export function snakeCase(str: string) {
  //replace capitals with space + lower case equivalent for later parsing
  str = str.replace(capitals, match => {
    return ' ' + (match.toLowerCase() || match)
  })
  return str.trim().split(wordSeparators).join('_')
}

//? Helpers used for camelCase function
const wordSeparatorsRegEx = /[\s\u2000-\u206F\u2E00-\u2E7F\\'!"#$%&()*+,\-./:;<=>?@[\]^_`{|}~]+/
const basicCamelRegEx = /^[a-z\u00E0-\u00FCA-Z\u00C0-\u00DC][\d|a-z\u00E0-\u00FCA-Z\u00C0-\u00DC]*$/
const fourOrMoreConsecutiveCapsRegEx = /([A-Z\u00C0-\u00DC]{4,})/g
const allCapsRegEx = /^[A-Z\u00C0-\u00DC]+$/

function deCap(match: string, endOfWord: boolean) {
  const arr = match.split('')
  const first = arr.shift()?.toUpperCase()
  const last = endOfWord ? arr.pop()?.toLowerCase() : arr.pop()
  return first + arr.join('').toLowerCase() + last
}

/**
 *
 * @param str string to be converted to camelCase
 * @returns camelCase string
 */
export function camelCase(str: string) {
  const words = str.split(wordSeparatorsRegEx)
  const len = words.length
  const mappedWords = Array.from({ length: len })
  for (let i = 0; i < len; i++) {
    let word = words[i] as string
    if (word === '') {
      continue
    }
    const isCamelCase = basicCamelRegEx.test(word) && !allCapsRegEx.test(word)
    if (isCamelCase) {
      word = word.replace(fourOrMoreConsecutiveCapsRegEx, (match, _p1, offset) => {
        return deCap(match, word.length - offset - match.length == 0)
      })
    }
    let firstLetter = word[0] as string
    firstLetter = i > 0 ? firstLetter.toUpperCase() : firstLetter.toLowerCase()
    mappedWords[i] = firstLetter + (!isCamelCase ? word.slice(1).toLowerCase() : word.slice(1))
  }
  return mappedWords.join('')
}

/**
 *
 * @param str string to be capitalized
 * @returns capitalized string
 */
export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 *
 * @param str string to be converted to Capital Case
 * @returns Capital Case string
 */
export function capitalCase(str: string) {
  const trimmed = str.trim()
  if (trimmed !== '' && !isNaN(Number(trimmed))) {
    return trimmed
  }
  return deCase(str).split(' ').map(capitalize).join(' ')
}

/**
 *
 * @param str string to have case removed
 * @returns string with case removed
 */
export function deCase(str: string) {
  const snakeString = snakeCase(str)
  return snakeString.split('_').join(' ')
}

export function formatBoolean(value: boolean | null | undefined) {
  if (value === null || value === undefined) {
    return 'N/A'
  }
  return value ? 'Yes' : 'No'
}

/**
 * Format a number as a percentage string with two decimal places
 * @param value - The number to format
 * @param places - Number of decimal places (default is 2)
 * @param placeholder Optional Placeholder to return if value is null or undefined
 * @returns The formatted percentage string
 */
export function formatPercent(value: number | string | null | undefined, places: number = 2, placeholder = '--') {
  if (value === null || value === undefined) return placeholder
  return `${Number(value).toFixed(places)}%`
}

/**
 * Formats a string to be used as a CSS class name by replacing non-word characters with hyphens and converting to lowercase
 * @param str
 * @returns
 */
export function formatCss(str: string) {
  return str.replace(/\W/g, '-').toLowerCase()
}

/** Truncates a string at the specified maximum length and adds ellipsis if needed
 * @param str The string to truncate
 * @param maxLength The maximum length of the string before truncation (default is 80; min is 4)
 * @returns The truncated string with ellipsis if it exceeds the maximum length
 *
 */
export function ellipsis(str: string, maxLength: number = 80) {
  if (maxLength < 4) {
    maxLength = 4
  }
  if (str.length <= maxLength) {
    return str
  }
  return str.slice(0, maxLength - 3) + '...'
}

export function relativePrice(price: 1 | 2 | 3 | 4 | '1' | '2' | '3' | '4' | null) {
  if (price === null) {
    return '--'
  }
  return '$'.repeat(Number(price))
}

/** Shows a Fabric Position wrapped in parentheses or an empty string if null
 *
 *  An Extremely redundant ternary
 */
export const fabricPositionLabel = (fabricPosition: number | null) =>
  `${fabricPosition != null ? `(${fabricPosition})` : ''}`
